// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// --- 인증 헤더 설정 (변경 없음) ---
const authHeader = sessionStorage.getItem('authHeader');
if (authHeader) {
  axios.defaults.headers.common['Authorization'] = authHeader;
} else {
  delete axios.defaults.headers.common['Authorization'];
}
// ------------------------------

const AdminDashboard = () => {
  const navigate = useNavigate();

  // --- 상태 변수 선언 ---
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [apiCalls, setApiCalls] = useState({});
  const [rateLimit, setRateLimit] = useState(null);
  const [newLimit, setNewLimit] = useState('');
  const [message, setMessage] = useState('');
  const [loadingError, setLoadingError] = useState('');
  // *** 시스템 프롬프트 표시 상태 추가 ***
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  // --------------------

  const baseURL = process.env.REACT_APP_API_URL || "http://localhost:3001";

  // --- 시간 포맷 함수 (변경 없음) ---
  const formatKST = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      return new Date(isoString).toLocaleString('ko-KR', {
        timeZone: 'Asia/Seoul',
        dateStyle: 'medium',
        timeStyle: 'medium',
        hour12: true
      });
    } catch (e) {
      console.error("시간 포맷 에러:", e);
      return isoString;
    }
  };
  // ------------------------------

  // --- 데이터 로딩 함수 (useCallback 제거, status 처리 수정) ---
  const fetchData = async (endpoint, setter, defaultValue) => {
    const currentAuthHeader = sessionStorage.getItem('authHeader');
    if (!currentAuthHeader) {
      // 인증 헤더 없을 시 처리 (기존 로직 유지)
      console.error("인증 헤더 없음, 데이터 로딩 중단.");
      setLoadingError("인증 정보가 없습니다. 다시 로그인해주세요.");
      navigate('/login');
      return;
    }
    try {
      const res = await axios.get(`${baseURL}${endpoint}`, {
        headers: { 'Authorization': currentAuthHeader }
      });
      // *** status 엔드포인트는 전체 응답 데이터를 상태로 설정 ***
      if (endpoint === '/api/admin/status') {
        setter(res.data);
      } else {
        // 다른 엔드포인트는 기존 로직 유지 (정렬 포함)
        setter(res.data.logs ? res.data.logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) : (res.data.apiCalls || res.data.rateLimit || res.data));
      }
      setLoadingError(''); // 성공 시 에러 초기화
    } catch (error) {
      // 에러 처리 (기존 로직 유지)
      console.error(`${endpoint} 불러오기 에러:`, error);
      if (error.response && error.response.status === 401) {
         setLoadingError("인증 실패. 다시 로그인해주세요.");
         sessionStorage.removeItem('authHeader');
         delete axios.defaults.headers.common['Authorization'];
         navigate('/login');
      } else {
        setLoadingError(`${endpoint} 데이터를 불러오는 중 오류가 발생했습니다.`);
      }
      setter(defaultValue); // 에러 시 기본값 설정
    }
  };
  // ---------------------------------------

  /* eslint-disable no-unused-vars */
  // --- 각 데이터 로딩 함수 정의 (useCallback 제거) ---
  const fetchStatus = () => fetchData('/api/admin/status', setStatus, null);
  const fetchLogs = () => fetchData('/api/admin/logs', setLogs, []);
  const fetchApiCalls = () => fetchData('/api/admin/api-calls', setApiCalls, {});
  const fetchRateLimit = () => fetchData('/api/admin/rate-limit', setRateLimit, null);
  /* eslint-enable no-unused-vars */
  // -------------------------------------------

  // --- 컴포넌트 마운트 및 폴링 설정 (useCallback 제거 및 의존성 배열 수정) ---
  useEffect(() => {
    if (!sessionStorage.getItem('authHeader')) {
        navigate('/login');
        return;
    }
    const fetchAllData = () => {
        // 직접 함수 호출
        fetchData('/api/admin/status', setStatus, null);
        fetchData('/api/admin/logs', setLogs, []);
        fetchData('/api/admin/api-calls', setApiCalls, {});
        fetchData('/api/admin/rate-limit', setRateLimit, null);
    };
    fetchAllData(); // 최초 호출
    const intervalId = setInterval(fetchAllData, 10000); // 폴링
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);
  // --------------------------------

  // --- 호출 제한 업데이트 함수 (변경 없음) ---
  const updateRateLimit = async (e) => {
    e.preventDefault();
    const currentAuthHeader = sessionStorage.getItem('authHeader');
    if (!currentAuthHeader) {
        setMessage("인증 정보가 없습니다. 다시 로그인해주세요.");
        navigate('/login');
        return;
    }
    const limitValue = Number(newLimit);
    // 유효성 검사 (기존 로직 유지)
    if (isNaN(limitValue) || !Number.isInteger(limitValue) || limitValue <= 0) {
        setMessage('유효한 양의 정수를 입력하세요.');
        return;
    }
    try {
      const res = await axios.post(`${baseURL}/api/admin/rate-limit`,
        { newLimit: limitValue },
        { headers: { 'Authorization': currentAuthHeader } }
      );
      setMessage(res.data.message);
      setRateLimit(limitValue);
      setNewLimit('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      // 에러 처리 (기존 로직 유지)
      console.error('호출 제한 업데이트 에러:', error);
       if (error.response && error.response.status === 401) {
         setMessage("인증 실패. 다시 로그인해주세요.");
         sessionStorage.removeItem('authHeader');
         delete axios.defaults.headers.common['Authorization'];
         navigate('/login');
      } else {
        setMessage(error.response?.data?.error || '호출 제한 업데이트 중 오류가 발생했습니다.');
      }
    }
  };
  // ---------------------------------------

  // --- 로그아웃 핸들러 (변경 없음) ---
  const handleLogout = () => {
    sessionStorage.removeItem('authHeader');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/login');
  };
  // ---------------------------------

  // --- 대화 내역 그룹핑 (변경 없음) ---
  const conversationLogs = logs.filter(
    log => log.path === '/api/generate' && log.conversation && log.response
  );
  const groupedConversations = conversationLogs.reduce((acc, curr) => {
    const ip = curr.ip;
    if (!acc[ip]) { acc[ip] = []; }
    acc[ip].unshift(curr);
    return acc;
  }, {});
  // ---------------------------------

  // --- *** 대화 내역 포매팅 컴포넌트 추가 (변경 없음) *** ---
  const FormattedConversation = ({ conversationString }) => {
    let conversation = [];
    try {
      conversation = JSON.parse(conversationString);
      if (!Array.isArray(conversation)) { throw new Error("Parsed data is not an array"); }
    } catch (error) {
      console.error("대화 내역 파싱 오류:", error);
      return <pre className="text-xs bg-red-100 p-2 rounded overflow-x-auto whitespace-pre-wrap break-all text-red-700">대화 내역 파싱 오류: {error.message}</pre>;
    }

    return (
      <div className="space-y-1 max-h-60 overflow-y-auto bg-gray-50 p-2 rounded border">
        {conversation.map((msg, index) => {
          if (!msg || typeof msg.role !== 'string' || !Array.isArray(msg.content) || msg.content.length === 0 || typeof msg.content[0].text !== 'string') {
            return <div key={index} className="text-xs text-red-500 italic">잘못된 메시지 형식</div>;
          }
          const isUser = msg.role === 'user';
          const bgColor = isUser ? 'bg-blue-100' : 'bg-green-100';
          const textAlign = isUser ? 'text-left' : 'text-right';
          const textColor = 'text-gray-800';
          const senderLabel = isUser ? '상대방(User)' : '나(Assistant)';

          return (
            <div key={index} className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
                <div className={`inline-block max-w-[80%] p-1.5 rounded ${bgColor} ${textAlign}`}>
                  <span className={`text-xs font-medium ${textColor}`}>{senderLabel}: </span>
                  <span className={`text-xs ${textColor}`}>{msg.content[0].text}</span>
                </div>
            </div>
          );
        })}
      </div>
    );
  };
  // ---------------------------------------------------

  // --- JSX 반환 부분 ---
  return (
    <div className="min-h-screen bg-gray-100 p-4 font-sans">
      {/* 헤더 영역 (변경 없음) */}
      <header className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-blue-600">관리자 대시보드</h1>
        <button onClick={handleLogout} className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded transition duration-300">
          로그아웃
        </button>
      </header>

      {/* 로딩 에러 메시지 (변경 없음) */}
      {loadingError && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 border border-red-400 rounded">
          {loadingError}
        </div>
      )}

      {/* 카드 레이아웃 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 서버 상태 카드 */}
        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition duration-300">
          <h2 className="text-xl font-semibold mb-4 border-b pb-2">서버 상태</h2>
          {status ? (
            <ul className="text-gray-700 space-y-2 text-sm">
              <li><strong>가동 시간:</strong> {Math.floor(status.uptime)}초</li>
              <li><strong>버전:</strong> {status.version}</li>
              <li><strong>현재 서버 시간(KST):</strong> {formatKST(status.timestamp)}</li>
              {/* --- *** 시스템 프롬프트 표시 로직 추가 *** --- */}
              <li>
                <div className="flex justify-between items-center">
                  <strong>시스템 프롬프트:</strong>
                  <button
                    onClick={() => setShowSystemPrompt(!showSystemPrompt)}
                    className="text-xs text-blue-500 hover:underline"
                  >
                    {showSystemPrompt ? '숨기기' : '보기'}
                  </button>
                </div>
                {showSystemPrompt && status.systemPrompt && (
                  <pre className="mt-1 p-2 bg-gray-100 border rounded text-xs whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                    {status.systemPrompt}
                  </pre>
                )}
                {showSystemPrompt && !status.systemPrompt && (
                  <p className="mt-1 text-xs text-gray-500 italic">시스템 프롬프트 정보 없음</p>
                )}
              </li>
              {/* ------------------------------------------ */}
            </ul>
          ) : (
            <p className="text-gray-500 text-sm">불러오는 중...</p>
          )}
        </div>

        {/* 추천 답변 호출 제한 카드 (변경 없음) */}
        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition duration-300">
          <h2 className="text-xl font-semibold mb-4 border-b pb-2">추천 답변 호출 제한</h2>
          <p className="text-gray-700 mb-2 text-sm">
            현재 하루 최대 호출 횟수: <span className="font-bold">{rateLimit ?? '불러오는 중...'}</span>
          </p>
          <form onSubmit={updateRateLimit} className="flex items-center space-x-2">
            <input
              type="number"
              min="1"
              value={newLimit}
              onChange={(e) => setNewLimit(e.target.value)}
              placeholder="새 제한값"
              className="px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300 w-28 text-sm"
            />
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-200 text-sm">
              저장
            </button>
          </form>
          {message && (
            <p className={`mt-2 text-xs ${message.includes('오류') || message.includes('유효한') || message.includes('실패') ? 'text-red-600' : 'text-green-600'}`}>
              {message}
            </p>
          )}
        </div>

        {/* API 호출 내역 카드 (변경 없음) */}
        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition duration-300">
          <h2 className="text-xl font-semibold mb-4 border-b pb-2">IP별 API 호출 내역</h2>
          {Object.keys(apiCalls).length > 0 ? (
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full border text-sm">
                <thead className="bg-blue-100 sticky top-0">
                  <tr>
                    <th className="py-2 px-4 border font-medium">IP 주소</th>
                    <th className="py-2 px-4 border font-medium">호출 횟수</th>
                    <th className="py-2 px-4 border font-medium">마지막 리셋 (KST)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(apiCalls).map(([ip, data]) => (
                    <tr key={ip} className="text-center hover:bg-gray-50">
                      <td className="py-2 px-4 border">{ip}</td>
                      <td className="py-2 px-4 border">{data.count}</td>
                      <td className="py-2 px-4 border">{formatKST(new Date(data.lastReset))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">{!apiCalls ? '불러오는 중...' : '호출 내역이 없습니다.'}</p>
          )}
        </div>

        {/* 로그 카드 (변경 없음) */}
        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition duration-300">
          <h2 className="text-xl font-semibold mb-4 border-b pb-2">전체 로그 기록 (최신순)</h2>
          {logs.length > 0 ? (
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full border text-sm">
                <thead className="bg-blue-100 sticky top-0">
                  <tr>
                    <th className="py-2 px-4 border font-medium">타임스탬프 (KST)</th>
                    <th className="py-2 px-4 border font-medium">IP 주소</th>
                    <th className="py-2 px-4 border font-medium">메소드</th>
                    <th className="py-2 px-4 border font-medium">경로</th>
                    <th className="py-2 px-4 border font-medium">상태</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={idx} className="text-center hover:bg-gray-50">
                      <td className="py-2 px-4 border">{formatKST(log.timestamp)}</td>
                      <td className="py-2 px-4 border">{log.ip}</td>
                      <td className="py-2 px-4 border">{log.method}</td>
                      <td className="py-2 px-4 border break-all">{log.path}</td>
                      <td className="py-2 px-4 border">{log.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">{!logs ? '불러오는 중...' : '기록이 없습니다.'}</p>
          )}
        </div>

        {/* --- 대화 및 추천 답변 기록 카드 (가독성 개선) --- */}
        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition duration-300 md:col-span-2">
          <h2 className="text-xl font-semibold mb-4 border-b pb-2">IP별 대화 및 추천 답변 기록 (최신순)</h2>
          {Object.keys(groupedConversations).length > 0 ? (
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {Object.entries(groupedConversations).map(([ip, convs]) => (
                <div key={ip} className="p-4 border rounded bg-gray-50">
                  <h3 className="font-bold text-lg mb-2 text-gray-800">IP: {ip}</h3>
                  {convs.map((entry, index) => (
                    <div key={index} className="mt-2 p-3 border rounded bg-white shadow-sm">
                      <p className="text-xs text-gray-500 mb-2">
                        <strong>시간 (KST):</strong> {formatKST(entry.timestamp)}
                      </p>
                      {/* --- *** 포매팅된 대화 내역 컴포넌트 사용 *** --- */}
                      <div className="mb-2">
                        <strong className="text-xs text-gray-700 block mb-1">대화 내역:</strong>
                        {entry.conversation ? (
                            <FormattedConversation conversationString={entry.conversation} />
                        ) : (
                            <p className="text-xs text-gray-500 italic">대화 내역 없음</p>
                        )}
                      </div>
                      <p className="text-xs text-gray-700">
                        <strong>추천 답변:</strong> <span className="text-blue-700 font-medium">{entry.response || '-'}</span>
                      </p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">대화 및 추천 답변 기록이 없습니다.</p>
          )}
        </div>
        {/* ----------------------------------------------- */}
      </div>
    </div>
  );
  // --- JSX 반환 부분 끝 ---
};

export default AdminDashboard;
